import React, { useCallback } from 'react';
import classnames from 'classnames';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Types
import { GoalStatusType, goalStatusTranslationKeys } from 'Shared/types/Goal.type';
import { GoalItem } from 'Shared/constants/types';

// Utils
import { getGoalStatusColor } from '../goal.utils';

// Components
import { WpButton } from 'Wpds/components/button';
import { WpDropdown, WpDropdownMenu, WpDropdownToggle, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import { WpIcon } from 'Wpds/components/icon';
import WpBadge from 'Wpds/components/badge/WpBadge';

// Styles
import './GoalStatus.scss';

type Props = {
  goal: GoalItem;
  showCompleteIndicator?: boolean;
  showLabel?: boolean;
  editable?: boolean;
};

const GoalStatus = ({ goal, showCompleteIndicator = true, showLabel = true, editable = false }: Props) => {
  const { tr } = useTranslation();
  const { canChangeStatus, handleUpdateGoal, isSaving } = useGoal({ slug: goal.slug });
  const status = goal.status;
  const isComplete = goal.complete;

  const statusOrderMap: Array<GoalStatusType> = ['no_status', 'on_track', 'at_risk', 'achieved', 'missed'];
  const baseClassName = 'GoalStatus';
  const classes = classnames(baseClassName, `${baseClassName}--${status}`);

  const handleSetStatusClick = useCallback((status: GoalStatusType) => {
    handleUpdateGoal({ status });
  }, []);

  if (canChangeStatus && editable && !isComplete) {
    return (
      <WpDropdown>
        <WpDropdownToggle>
          <WpButton variant="tertiary" square slimPadding loading={isSaving}>
            <WpIcon prefix="fas" name="circle" size="xs" color={getGoalStatusColor({ status })} />
            {showLabel && tr(goalStatusTranslationKeys[status])}
            <WpIcon name="angle-down" />
          </WpButton>
        </WpDropdownToggle>
        <WpDropdownMenu>
          {statusOrderMap.map(status => (
            <WpDropdownMenuItem
              key={`goal-status-${status}`}
              onClick={() => handleSetStatusClick(status)}
              // qaTest="goal-card-dropdown-status-change-at-risk"
            >
              <WpIcon prefix="fas" name="circle" color={getGoalStatusColor({ status })} size="xs" />
              {tr(goalStatusTranslationKeys[status])}
            </WpDropdownMenuItem>
          ))}
        </WpDropdownMenu>
      </WpDropdown>
    );
  }

  return (
    <div className={classes}>
      {showCompleteIndicator && isComplete && (
        <div className={`${baseClassName}__badge`}>
          <WpBadge color="green">{tr('perform.goals.status.complete')}</WpBadge>
        </div>
      )}
      <span className={`${baseClassName}__indicator`}>
        <WpIcon prefix="fas" name="circle" size="xs" color={getGoalStatusColor({ status })} />
      </span>
      {showLabel && <>{tr(goalStatusTranslationKeys[status])}</>}
    </div>
  );
};

export default GoalStatus;
